import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { ChevronDown, Menu, X } from 'lucide-react'
import { useAuth, useLogout } from '@/hooks/use-auth'
import { NotificationBell } from './notification-bell'
import { sidebarNavByRole } from './nav-config'
import type { AuthUser } from '@/lib/auth'

function profilePathForUser(user: AuthUser): string {
  if (user.role === 'admin') return `/admin/members/${user.id}`
  if (user.role === 'estate_manager') return '/profile'
  return '/profile'
}
import { cn } from '@/lib/utils'

export function Topbar({ title }: { title?: string }) {
  const { user } = useAuth()
  const logout = useLogout()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const drawerNav = user ? sidebarNavByRole[user.role] : []

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '??'

  useEffect(() => {
    if (!menuOpen) return

    function close() {
      setMenuOpen(false)
    }

    function onPointerDown(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close()
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!drawerOpen) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  async function handleLogout() {
    // An already-expired session makes logout fail; the cache is cleared either way
    await logout.mutateAsync().catch(() => undefined)
    setMenuOpen(false)
    navigate('/login', { replace: true })
  }

  return (
    <>
      <header className="flex h-topbar shrink-0 items-center justify-between border-b border-border bg-cream px-4 sm:px-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 flex size-9 items-center justify-center rounded text-ink transition-colors hover:bg-cream-2 lg:hidden"
          >
            <Menu size={20} />
          </button>
          {title && (
            <h1 className="font-serif text-2xl font-medium tracking-tight text-ink lg:hidden">
              {title}
            </h1>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <NotificationBell />

          <div ref={menuRef} className="relative">
            <button
              type="button"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="Account menu"
              onClick={() => setMenuOpen((open) => !open)}
              className={cn(
                'flex items-center gap-2 rounded py-1 pl-1 pr-2 transition-colors duration-150',
                'hover:bg-cream-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/40',
                menuOpen && 'bg-cream-2',
              )}
            >
              <div className="flex size-[34px] items-center justify-center rounded-full bg-navy-2">
                <span className="font-serif text-sm text-cream">{initials}</span>
              </div>
              <span className="hidden font-sans text-sm font-medium text-ink sm:inline">
                {user?.name}
              </span>
              <ChevronDown
                size={14}
                className={cn(
                  'hidden text-muted-foreground transition-transform sm:inline',
                  menuOpen && 'rotate-180',
                )}
              />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-1 min-w-[11rem] border border-border bg-card py-1"
              >
                <Link
                  to={user ? profilePathForUser(user) : '/profile'}
                  role="menuitem"
                  className="block px-4 py-2.5 font-sans text-sm text-ink transition-colors hover:bg-cream-2"
                  onClick={() => setMenuOpen(false)}
                >
                  Profile
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  disabled={logout.isPending}
                  onClick={handleLogout}
                  className="block w-full px-4 py-2.5 text-left font-sans text-sm text-ink transition-colors hover:bg-cream-2 disabled:opacity-50"
                >
                  {logout.isPending ? 'Signing out…' : 'Sign out'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-ink/40"
          />
          <nav className="relative flex h-full w-[280px] max-w-[80vw] flex-col bg-navy shadow-xl">
            <div className="flex items-center justify-between border-b border-cream/10 px-6 py-5">
              <p className="font-serif text-xl font-medium tracking-[0.06em] text-cream uppercase">
                Riverpark
              </p>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                className="flex size-8 items-center justify-center rounded text-cream/60 hover:bg-cream/10 hover:text-cream"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
              {drawerNav.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded px-3 py-2.5 font-sans text-sm font-medium transition-colors duration-150',
                      isActive
                        ? 'border-l-[3px] border-green-lt bg-cream/10 pl-[9px] text-cream/95'
                        : 'border-l-[3px] border-transparent text-cream/50 hover:bg-cream/5 hover:text-cream/80',
                    )
                  }
                >
                  <Icon size={18} strokeWidth={1.75} aria-hidden />
                  {label}
                </NavLink>
              ))}
            </div>
          </nav>
        </div>
      )}
    </>
  )
}
